import { IntegerProblem, Inequality } from '@/core/cuts'
import { generateVerticesFromConstraints } from '@/graphics/geometry'
import { getCutInfo } from '@/core/cuts'
import { SimplexSolver } from '@/core/simplex'
import { appBus } from '@/app/app-events'
import { logger } from '@/app/logger'

export type BranchBound = {
  varIndex: number // 0=x,1=y,2=z
  type: 'le' | 'ge'
  value: number
}

export type BnBNodeStatus = 'open' | 'branched' | 'fathomed' | 'pruned'

export type PruneReason = 'infeasible' | 'bound'

export interface BnBNode {
  id: string
  parentId?: string
  depth: number
  bounds: BranchBound[]
  problem: IntegerProblem
  lp: {
    optimalValue: number
    solution: number[]
    isIntegral: boolean
    hasFractionalSolution: boolean
    infeasible?: boolean
  }
  status: BnBNodeStatus
  pruneReason?: PruneReason
  children: string[]
}

export interface BnBState {
  rootNodeId: string
  nodes: Map<string, BnBNode>
  currentNodeId: string
  incumbent?: { value: number, solution: number[] }
  stats: { created: number, pruned: number, fathomed: number }
  maxNodes: number
}

let nodeCounter = 0
function nextNodeId(): string { return `N${++nodeCounter}` }

export class BnBManager {
  private state: BnBState | null = null

  constructor(private defaultMaxNodes: number = 7) { }

  getState(): BnBState | null { return this.state }

  // LP 评估适配器：统一从 cuts 层获取 LP 解并兜底
  private evaluateLP(problem: IntegerProblem, bounds: BranchBound[]): {
    optimalValue: number
    solution: number[]
    hasFractionalSolution: boolean
    infeasible: boolean
  } {
    // Build lower/upper bounds from branch bounds
    const n = problem.c.length
    const lb = new Array(n).fill(0)
    const ub = new Array(n).fill(Number.POSITIVE_INFINITY)
    for (const b of bounds) {
      if (b.type === 'ge') lb[b.varIndex] = Math.max(lb[b.varIndex], b.value)
      else ub[b.varIndex] = Math.min(ub[b.varIndex], b.value)
    }

    // Shift variables: x = y + lb, y >= 0
    try {
      const A: number[][] = []
      const B: number[] = []
      // Transform original constraints except non-negativity rows (-e_i <= 0)
      for (let i = 0; i < problem.A.length; i++) {
        const alpha = problem.A[i]
        const beta = problem.b[i]
        const isLowerBoundRow = alpha.some((v, idx) => (v === -1 && idx < n)) && alpha.reduce((s, v) => s + (v !== 0 ? 1 : 0), 0) === 1 && Math.abs(beta) < 1e-12
        if (isLowerBoundRow) continue // will re-add -y <= 0
        const shift = alpha.reduce((s, aij, j) => s + aij * lb[j], 0)
        A.push(alpha.slice(0, n))
        B.push(beta - shift)
      }
      // Add -y <= 0 for nonnegativity
      for (let j = 0; j < n; j++) {
        const row = new Array(n).fill(0); row[j] = -1
        A.push(row)
        B.push(0)
      }
      // Add upper bounds if any: y_j <= ub_j - lb_j
      for (let j = 0; j < n; j++) {
        if (Number.isFinite(ub[j])) {
          const rhs = ub[j] - lb[j]
          if (rhs < 0) {
            return { optimalValue: Number.NEGATIVE_INFINITY, solution: [], hasFractionalSolution: false, infeasible: true }
          }
          const row = new Array(n).fill(0); row[j] = 1
          A.push(row)
          B.push(rhs)
        }
      }

      // Quick infeasibility check: if a row has all non-negative coefficients and RHS < 0, infeasible
      {
        const tol = 1e-10
        for (let i = 0; i < A.length; i++) {
          const allNonNeg = A[i].every((aij) => aij >= -tol)
          if (allNonNeg && B[i] < -tol) {
            return { optimalValue: Number.NEGATIVE_INFINITY, solution: [], hasFractionalSolution: false, infeasible: true }
          }
        }
      }

      // Solve on transformed problem
      const res = SimplexSolver.getOptimalTableau(A, B, problem.c.slice(0, n))
      if (!res.isOptimal) {
        return { optimalValue: Number.NEGATIVE_INFINITY, solution: [], hasFractionalSolution: false, infeasible: true }
      }
      const y = res.optimalSolution.slice(0, n).map((f: any) => f.toNumber())
      const x = y.map((v: number, j: number) => v + lb[j])
      // Compute objective value c^T x
      const val = x.reduce((s: number, v: number, j: number) => s + (problem.c[j] || 0) * v, 0)
      const hasFrac = x.some((v: number) => Math.abs(v - Math.round(v)) > 1e-9)
      return { optimalValue: val, solution: x, hasFractionalSolution: hasFrac, infeasible: false }
    } catch (_e) {
      // Fallback to original method if something goes wrong
      try {
        const info = getCutInfo(problem)
        const solution = (info?.optimalSolution || []).slice(0, 3)
        const value = info?.optimalValue ?? Number.NEGATIVE_INFINITY
        const hasFrac = !!info?.hasFractionalSolution
        const infeasible = !Array.isArray(solution) || solution.length === 0 || !Number.isFinite(value)
        return { optimalValue: value, solution, hasFractionalSolution: hasFrac, infeasible }
      } catch {
        return { optimalValue: Number.NEGATIVE_INFINITY, solution: [], hasFractionalSolution: false, infeasible: true }
      }
    }
  }

  start(rootProblem: IntegerProblem, maxNodes?: number): BnBState {
    nodeCounter = 0
    const id = nextNodeId()
    const rootNode = this.createNode(id, undefined, 0, [], rootProblem)
    const nodes = new Map<string, BnBNode>()
    nodes.set(rootNode.id, rootNode)
    this.state = {
      rootNodeId: id,
      nodes,
      currentNodeId: id,
      incumbent: undefined,
      stats: { created: 1, pruned: 0, fathomed: 0 },
      maxNodes: maxNodes ?? this.defaultMaxNodes,
    }
    appBus.emit('bnb-started', { rootNodeId: id })
    // 激活根节点，保证 UI 和三维能立即更新
    appBus.emit('bnb-node-activated', { nodeId: id, node: rootNode })
    // 发送一次更新，便于树视图建立
    appBus.emit('bnb-node-updated', { node: rootNode })
    this.updateIncumbentIfIntegral(rootNode)
    this.maybeFinish()
    return this.state
  }

  setMaxNodes(limit: number): void {
    if (!this.state) return
    this.state.maxNodes = Math.max(1, Math.floor(limit))
  }

  selectNode(nodeId: string): BnBNode | null {
    if (!this.state) return null
    const node = this.state.nodes.get(nodeId) || null
    if (node) {
      this.state.currentNodeId = nodeId
      appBus.emit('bnb-node-activated', { nodeId, node })
    }
    return node
  }

  getCurrentNode(): BnBNode | null {
    if (!this.state) return null
    return this.state.nodes.get(this.state.currentNodeId) || null
  }

  branchOnVariable(varIndex: number): { left?: BnBNode, right?: BnBNode } {
    const state = this.state
    if (!state) throw new Error('BnB not started')
    const parent = this.getCurrentNode()
    if (!parent) throw new Error('No current node')
    if (parent.status !== 'open') {
      logger.warn('[BnB] Cannot branch: node not open')
      return {}
    }

    const sol = parent.lp.solution
    if (!sol || sol.length <= varIndex) {
      logger.warn('[BnB] No LP solution available to branch on')
      return {}
    }
    const value = sol[varIndex]
    const floorVal = Math.floor(value)
    const ceilVal = Math.floor(value) + 1

    // Enforce node limit
    const spaceLeft = state.maxNodes - state.stats.created
    if (spaceLeft <= 0) {
      logger.warn('[BnB] Node limit reached; cannot branch')
      return {}
    }

    const leftBound: BranchBound = { varIndex, type: 'le', value: floorVal }
    const rightBound: BranchBound = { varIndex, type: 'ge', value: ceilVal }

    const leftNode = this.tryCreateChild(parent, [leftBound])
    let rightNode: BnBNode | undefined
    if (state.stats.created < state.maxNodes) {
      rightNode = this.tryCreateChild(parent, [rightBound])
    }

    if (leftNode || rightNode) {
      parent.status = 'branched'
      if (leftNode) parent.children.push(leftNode.id)
      if (rightNode) parent.children.push(rightNode.id)
      appBus.emit('bnb-node-updated', { node: parent })
      // 保持当前节点选中，但强制刷新该节点的几何（会显示子节点的并集）
      if (this.state) {
        this.state.currentNodeId = parent.id
      }
      appBus.emit('bnb-node-activated', { nodeId: parent.id, node: parent })
    }
    this.maybeFinish()
    return { left: leftNode, right: rightNode }
  }

  branchOne(varIndex: number, side: 'le' | 'ge'): BnBNode | undefined {
    const state = this.state
    if (!state) throw new Error('BnB not started')
    const parent = this.getCurrentNode()
    if (!parent) throw new Error('No current node')
    if (parent.status !== 'open') return undefined
    if (state.stats.created >= state.maxNodes) return undefined

    const sol = parent.lp.solution
    if (!sol || sol.length <= varIndex) return undefined
    const value = sol[varIndex]
    const floorVal = Math.floor(value)
    const ceilVal = Math.floor(value) + 1

    const bound: BranchBound = side === 'le'
      ? { varIndex, type: 'le', value: floorVal }
      : { varIndex, type: 'ge', value: ceilVal }

    const child = this.tryCreateChild(parent, [bound])
    if (child) {
      parent.status = 'branched'
      parent.children.push(child.id)
      appBus.emit('bnb-node-updated', { node: parent })
      // 同样保持父节点为当前节点，并刷新其几何
      if (this.state) {
        this.state.currentNodeId = parent.id
      }
      appBus.emit('bnb-node-activated', { nodeId: parent.id, node: parent })
    }
    this.maybeFinish()
    return child
  }

  private tryCreateChild(parent: BnBNode, extraBounds: BranchBound[]): BnBNode | undefined {
    const state = this.state!
    if (state.stats.created >= state.maxNodes) return undefined

    const id = nextNodeId()
    const bounds = [...parent.bounds, ...extraBounds]
    const problem = this.buildProblemWithBounds(state.nodes.get(state.rootNodeId)!.problem, bounds)
    const node = this.createNode(id, parent.id, parent.depth + 1, bounds, problem)
    state.nodes.set(id, node)
    state.stats.created += 1
    appBus.emit('bnb-node-updated', { node })
    this.updateIncumbentIfIntegral(node)
    return node
  }

  private createNode(id: string, parentId: string | undefined, depth: number, bounds: BranchBound[], problem: IntegerProblem): BnBNode {
    // Evaluate LP relaxation
    let { optimalValue, solution, hasFractionalSolution, infeasible } = this.evaluateLP(problem, bounds)

    // Extra geometry analysis via vertex enumeration:
    //  - If the feasible region appears to have no vertices at all (empty),
    //  - or collapses to a single non-integer point,
    // then we treat the node as infeasible for BnB purposes.
    //  - Otherwise, we also use the enumerated vertices to "snap" the LP
    //    solution to a true geometric vertex of the current polyhedron so
    //    that the orange LP marker in 3D always sits on a vertex (BFS) and
    //    matches the BnB panel values exactly.
    let emptyRegion = false
    let singlePointNonInteger = false
    try {
      const verts = generateVerticesFromConstraints(problem.inequalities)
      if (Array.isArray(verts)) {
        if (verts.length === 0) {
          // In 3D, a non-empty bounded polyhedron must have at least
          // one vertex. If we cannot enumerate any vertices at all
          // while the root problem is bounded, this branch is
          // effectively empty (or numerically indistinguishable from
          // empty), so we mark it as infeasible.
          emptyRegion = true
        } else if (verts.length === 1) {
          const v = verts[0]
          const isInt =
            Math.abs(v.x - Math.round(v.x)) < 1e-9 &&
            Math.abs(v.y - Math.round(v.y)) < 1e-9 &&
            Math.abs(v.z - Math.round(v.z)) < 1e-9
          singlePointNonInteger = !isInt
        } else {
          // Region has at least one vertex. Use the vertex set to ensure
          // that our LP solution aligns with an actual polyhedron vertex
          // for both numerical robustness and visual consistency.
          try {
            const c = problem.c || []
            let bestVal = Number.NEGATIVE_INFINITY
            const bestVerts: typeof verts = []

            for (const v of verts) {
              const val =
                (c[0] || 0) * v.x +
                (c[1] || 0) * v.y +
                (c[2] || 0) * v.z
              if (val > bestVal + 1e-9) {
                bestVal = val
                bestVerts.length = 0
                bestVerts.push(v)
              } else if (Math.abs(val - bestVal) <= 1e-9) {
                bestVerts.push(v)
              }
            }

            if (bestVerts.length > 0 && Number.isFinite(bestVal)) {
              // Pick the vertex closest to the simplex solution (to avoid
              // large visual jumps when there are multiple optimal vertices).
              let chosen = bestVerts[0]
              if (Array.isArray(solution) && solution.length >= 3) {
                let bestDist = Infinity
                for (const v of bestVerts) {
                  const dx = v.x - solution[0]
                  const dy = v.y - solution[1]
                  const dz = v.z - solution[2]
                  const d2 = dx * dx + dy * dy + dz * dz
                  if (d2 < bestDist) {
                    bestDist = d2
                    chosen = v
                  }
                }
              }

              const snapped = [chosen.x, chosen.y, chosen.z]
              const snappedHasFrac = snapped.some((vv) => Math.abs(vv - Math.round(vv)) > 1e-9)

              // If there is a noticeable discrepancy between the simplex
              // value and the vertex-based value, log it for debugging but
              // still trust the vertex-based value for visualization / BnB.
              if (Number.isFinite(optimalValue) && Math.abs(bestVal - optimalValue) > 1e-6) {
                logger.warn('[BnB] Vertex-based LP value differs from simplex value at node', {
                  simplexValue: optimalValue,
                  vertexValue: bestVal,
                  simplexSolution: solution,
                  snappedSolution: snapped,
                })
              }

              optimalValue = bestVal
              solution = snapped
              hasFractionalSolution = snappedHasFrac
            }
          } catch (snapErr) {
            logger.warn('[BnB] Failed to snap LP solution to vertex:', snapErr)
          }
        }
      }
    } catch (e) {
      logger.warn('[BnB] single-point detection failed:', e)
    }

    // NOTE: DO NOT prune by "no integer lattice point"; per requested rule, only prune
    // when LP is infeasible, when the region is detected as EMPTY via vertex
    // enumeration, or when the feasible region collapses to a SINGLE point and
    // that point is non-integer.


    const isIntegral = this.isIntegralSolution(solution)

    let status: BnBNodeStatus
    let pruneReason: PruneReason | undefined
    if (infeasible || emptyRegion || singlePointNonInteger) {
      status = 'pruned'
      pruneReason = 'infeasible'
    } else if (isIntegral) {
      status = 'fathomed'
    } else {
      status = 'open'
    }

    // Prune by incumbent bound if not already pruned/fathomed
    if (status === 'open' && this.state?.incumbent && Number.isFinite(optimalValue)) {
      const inc = this.state.incumbent.value
      if (optimalValue <= inc + 1e-9) {
        status = 'pruned'
        pruneReason = 'bound'
      }
    }

    const node: BnBNode = {
      id,
      parentId,
      depth,
      bounds,
      problem,
      lp: { optimalValue, solution, isIntegral, hasFractionalSolution, infeasible },
      status,
      pruneReason,
      children: [],
    }
    if (status === 'fathomed') this.state && (this.state.stats.fathomed += 1)
    if (status === 'pruned') this.state && (this.state.stats.pruned += 1)
    return node
  }

  private isIntegralSolution(sol: number[], eps: number = 1e-8): boolean {
    if (!sol || sol.length === 0) return false
    for (let i = 0; i < Math.min(3, sol.length); i++) {
      const v = sol[i]
      if (!Number.isFinite(v)) return false
      if (Math.abs(v - Math.round(v)) > eps) return false
    }
    return true
  }

  private buildProblemWithBounds(base: IntegerProblem, bounds: BranchBound[]): IntegerProblem {
    const inequalities: Inequality[] = [...base.inequalities]
    for (const b of bounds) {
      if (b.type === 'le') {
        const alpha = [0, 0, 0]; alpha[b.varIndex] = 1
        inequalities.push({ alpha, beta: b.value })
      } else {
        const alpha = [0, 0, 0]; alpha[b.varIndex] = -1
        inequalities.push({ alpha, beta: -b.value })
      }
    }
    const A = inequalities.map(inq => inq.alpha.slice(0, 3))
    const bvec = inequalities.map(inq => inq.beta)
    return { A, b: bvec, c: base.c.slice(0, 3), inequalities }
  }

  private updateIncumbentIfIntegral(node: BnBNode): void {
    if (!this.state) return
    if (node.lp.infeasible) return
    if (!node.lp.isIntegral) return

    const currentInc = this.state.incumbent
    const newVal = node.lp.optimalValue

    // For maximization: only react when we truly improve the incumbent
    if (currentInc && !(newVal > currentInc.value + 1e-9)) {
      return
    }

    // 1) Update incumbent
    this.state.incumbent = { value: newVal, solution: node.lp.solution }
    appBus.emit('bnb-incumbent-updated', { value: newVal, solution: node.lp.solution, nodeId: node.id })

    // 2) Global bound-based pruning: any OPEN node whose LP value is
    //    not better than the incumbent cannot yield a better integer solution.
    const tol = 1e-9
    for (const n of this.state.nodes.values()) {
      if (n.status !== 'open') continue
      const z = n.lp?.optimalValue
      if (!Number.isFinite(z)) continue
      if (z <= newVal + tol) {
        n.status = 'pruned'
        n.pruneReason = 'bound'
        this.state.stats.pruned += 1
        appBus.emit('bnb-node-updated', { node: n })
      }
    }
  }

  private maybeFinish(): void {
    if (!this.state) return
    // Finish if there are no open nodes remaining
    for (const n of this.state.nodes.values()) {
      if (n.status === 'open') return
    }
    appBus.emit('bnb-finished', { incumbent: this.state.incumbent, stats: this.state.stats })
  }

  backtrack(nodeId: string): void {
    const state = this.state
    if (!state) return

    const targetNode = state.nodes.get(nodeId)
    if (!targetNode) {
      logger.warn(`[BnB] Cannot backtrack to non-existent node ${nodeId}`)
      return
    }

    // 1. Collect all descendants to remove
    const toRemove = new Set<string>()
    const collectDescendants = (id: string) => {
      const n = state.nodes.get(id)
      if (!n) return
      for (const childId of n.children) {
        toRemove.add(childId)
        collectDescendants(childId)
      }
    }
    collectDescendants(nodeId)

    // 2. Remove them from map and update stats
    // Note: We need to recalculate stats from scratch or carefully decrement.
    // Recalculating is safer.
    for (const id of toRemove) {
      state.nodes.delete(id)
    }

    // 3. Reset target node
    targetNode.children = []
    targetNode.status = 'open'
    // If it was pruned/fathomed, it is now open, so we might need to re-evaluate?
    // Actually, if we backtrack, we assume we want to branch from it again.
    // Its LP solution hasn't changed.

    // 4. Recalculate global stats
    let created = 0
    let pruned = 0
    let fathomed = 0
    let maxIncumbentVal = Number.NEGATIVE_INFINITY
    let bestIncumbent: { value: number, solution: number[] } | undefined = undefined

    for (const n of state.nodes.values()) {
      created++
      if (n.status === 'pruned') pruned++
      if (n.status === 'fathomed') {
        fathomed++
        if (n.lp.isIntegral && n.lp.optimalValue > maxIncumbentVal) {
          maxIncumbentVal = n.lp.optimalValue
          bestIncumbent = { value: n.lp.optimalValue, solution: n.lp.solution }
        }
      }
    }

    state.stats = { created, pruned, fathomed }
    state.incumbent = bestIncumbent
    state.currentNodeId = nodeId

    // 5. Emit updates
    // We emit a special 'backtracked' event with the full new state to ensure sync
    appBus.emit('bnb-backtracked', {
      state: {
        nodes: Array.from(state.nodes.entries()),
        rootNodeId: state.rootNodeId,
        currentNodeId: state.currentNodeId,
        incumbentNodeId: bestIncumbent ? this.findNodeIdBySolution(state, bestIncumbent.solution) : null
      }
    })

    // Also emit activation for the target node
    appBus.emit('bnb-node-activated', { nodeId, node: targetNode })

    // If incumbent changed (e.g. was lost), emit update
    if (!bestIncumbent) {
      // If we lost the incumbent, we need to tell UI
      // We can emit a null incumbent update or just rely on the full state sync above
      // But let's be explicit if needed. The 'bnb-backtracked' should handle it.
    } else {
      appBus.emit('bnb-incumbent-updated', {
        value: bestIncumbent.value,
        solution: bestIncumbent.solution,
        nodeId: this.findNodeIdBySolution(state, bestIncumbent.solution)
      })
    }
  }

  private findNodeIdBySolution(state: BnBState, sol: number[]): string | undefined {
    for (const [id, node] of state.nodes) {
      if (node.lp.solution === sol) return id // Reference equality might work if we didn't copy
      // Deep check
      if (node.lp.solution.length === sol.length && node.lp.solution.every((v, i) => Math.abs(v - sol[i]) < 1e-9)) return id
    }
    return undefined
  }
}
